import React, { useState, useEffect, type FC, useRef } from 'react';
import { Loader } from '../icons/OurIcons';

import useGetPropertyDetails from '../../hooks/api/Property/useGetPropertyDetails';
import { getPropertyImages } from '../../utility/utility_functions';

import '../../../assets/stylesheets/property-image-galleria.css';
import { AppDispatch } from '../../redux/store';
import { useDispatch } from 'react-redux';
import { setMapKey } from '../../redux/state/mapSlice';
import useGetMostRecentAvmResults from '../../hooks/api/AvmResults/useGetMostRecentAvmResults';
import { fetchFavoritesThunk } from '../../redux/state/favoriteSlice';
import { setStreetViewKey } from '../../redux/state/streetViewSlice';
import { CompsAndRepairsValues } from './CompsAndRepairsValues';
import { PropertyDetailsList } from './PropertyDetailsList';
import { PriceIncomeMetricsList } from './PriceIncomeMetricsList';
import { AvmCompsDisplay } from './get-more-data-all-users/avm/AvmCompsDisplay';
import { ViewOptions } from './PropertyDetailsViewSelect';
import { PropertyDetailsHeader } from './PropertyDetailsHeader';
import { WholesaleDealPanel } from './WholesaleDealPanel';
import { PropertyMapOrImages } from './PropertyMapOrImages';
import { GalleriaRefContext } from '../../contexts/GalleriaRefContext';
import useGetPropertyAgents from '../../hooks/api/Property/useGetPropertyAgents';
import { DisclaimerContainer } from '../common/disclaimers/DisclaimerContainer';
import { PropertyDisclaimer } from '../common/disclaimers/PropertyDisclaimers';
import { PropertyDetailError } from './PropertyDetailError';
import { PropertyDetailAgentHandler } from './propertyDetailAgent/PropertyDetailAgentHandler';
import { PropertyCustomData, PropertyCustomDataRow } from './PropertyCustomData';
import { useGetValuationSettings } from '../../hooks/api/ValuationSettings/useGetValuationSettings';
import { DEFAULT_MARKET_VALUE_RATE, applyMarketValueRateToAvmResult } from '../../utility/valuationSettings';

export type PropertyDetailsProps = {
  propertyId: string;
  mapKey: string;
  streetViewKey: string;
};

const PropertyDetails: FC<PropertyDetailsProps> = ({
  propertyId,
  mapKey,
  streetViewKey,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [activeAvmResult, setActiveAvmResult] = useState(undefined);
  const [soldAvmResult, setSoldAvmResult] = useState(undefined);
  const [activeView, setActiveView] = useState(ViewOptions.images);
  const [propertyImages, setPropertyImages] = useState([]);

  const galleriaRef = useRef(null);

  const { propertyDetails, isLoadingPropertyDetails, errorPropertyDetails } =
    useGetPropertyDetails(propertyId);

  const {
    mostRecentAvmResults,
    isLoadingMostRecentAvmResults,
    errorMostRecentAvmResults,
  } = useGetMostRecentAvmResults(propertyId);

  const {
    listingAgent,
    isLoading: isLoadingPropertyAgents,
    errorGetPropertyAgents,
  } = useGetPropertyAgents(propertyId);

  // The Sold and Active ARV shown here include the user's Market Value Rate (0% if it can't load).
  const { data: valuationSettings, isLoading: isLoadingValuationSettings } = useGetValuationSettings();
  const marketValueRate = valuationSettings?.market_value_rate ?? DEFAULT_MARKET_VALUE_RATE;

  const isLoading = isLoadingMostRecentAvmResults || isLoadingPropertyDetails || isLoadingValuationSettings;

  useEffect(() => {
    dispatch(setMapKey(mapKey));
    dispatch(setStreetViewKey(streetViewKey));
    dispatch(fetchFavoritesThunk());
  }, []);

  useEffect(() => {
    if (mostRecentAvmResults) {
      setActiveAvmResult(applyMarketValueRateToAvmResult(mostRecentAvmResults?.active_avm_result, marketValueRate));
      setSoldAvmResult(applyMarketValueRateToAvmResult(mostRecentAvmResults?.sold_avm_result, marketValueRate));
    }
  }, [mostRecentAvmResults, marketValueRate]);

  useEffect(() => {
    if (propertyDetails) {
      setPropertyImages(getPropertyImages(propertyDetails));
    }
  }, [propertyDetails]);

  if (errorPropertyDetails) {
    return (
      <PropertyDetailError
        error={errorPropertyDetails}
        title="property details"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="w-screen content-center px-10">
        <div className="mx-auto mt-10 w-96">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl">
      <div className=" mx-6 mb-4">
        <PropertyDetailsHeader
          propertyDetails={propertyDetails}
          avmEstimatedValue={soldAvmResult?.result?.estimated_value}
          activeView={activeView}
          setActiveView={setActiveView}
        />
      </div>
      <WholesaleDealPanel propertyId={propertyDetails?.id} />
      <GalleriaRefContext.Provider value={galleriaRef}>
        <PropertyMapOrImages
          activeView={activeView}
          property={propertyDetails}
          mlsImages={propertyImages}
        />
      </GalleriaRefContext.Provider>
      {!errorMostRecentAvmResults && (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 m-4 py-2">
            <PropertyDetailsList propertyDetails={propertyDetails} />
            <PriceIncomeMetricsList
              propertyDetails={propertyDetails}
              soldAvmResult={soldAvmResult?.result}
            />
          </div>
          <CompsAndRepairsValues
            activeComps={activeAvmResult}
            soldComps={soldAvmResult}
            propertyDetails={propertyDetails}
          />
          <div className="m-6">
            <AvmCompsDisplay
              propertyDetails={propertyDetails}
              soldComps={soldAvmResult}
              activeComps={activeAvmResult}
            />
          </div>
        </>
      )}
      {errorMostRecentAvmResults && (
        <PropertyDetailError
          error={errorMostRecentAvmResults}
          title="AVM results"
        />
      )}
      <PropertyCustomData listUploadsPropertiesData={propertyDetails.list_uploads_properties_data as PropertyCustomDataRow[]} />
      <PropertyDetailAgentHandler
        agent={listingAgent}
        isLoading={isLoadingPropertyAgents}
        error={errorGetPropertyAgents}
        publicRemarks={propertyDetails.public_remarks}
      />
      <DisclaimerContainer>
        <PropertyDisclaimer
          brokerageName={listingAgent?.office_name}
          agentName={`${listingAgent?.first_name} ${listingAgent?.last_name}`}
          updatedAt={propertyDetails.updated_at}
        />
      </DisclaimerContainer>
    </div>
  );
};

export default PropertyDetails;
